import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import {
  enqueueCartMutation,
  requestCartJson,
  collectCartSectionIds,
  applyCartSectionsResponse,
  extractCartItemCountFromHtml,
  setCartItemsLocked,
} from '@theme/cart-section-updater';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #isRemovingExcludedItems = false;

  #excludedProductType = 'recycling fee';

  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    void this.#removeExcludedItems();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    setCartItemsLocked(true);

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    cartTotal?.shimmer();

    const sectionsParam = collectCartSectionIds(this.sectionId);

    enqueueCartMutation(async () => {
      try {
        const parsedResponseText = await requestCartJson(Theme.routes.cart_change_url, {
          line,
          quantity,
          sections: sectionsParam,
          sections_url: window.location.pathname,
        });

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        this.#updateQuantitySelectors(parsedResponseText);

        const newCartItemCount = extractCartItemCountFromHtml(
          parsedResponseText.sections?.[this.sectionId]
        );

        applyCartSectionsResponse(parsedResponseText, {
          sourceSectionId: this.sectionId,
          source: 'cart-items-component',
          itemCount: newCartItemCount,
          eventTarget: this,
        });

        this.#updateCartQuantitySelectorButtonStates();
      } catch (error) {
        console.error(error);
      } finally {
        setCartItemsLocked(false);
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      }
    });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    if (event.detail?.data?.morphHandled) {
      this.#updateCartQuantitySelectorButtonStates();
      return;
    }

    if (!event.detail?.data?.skipDepositCleanup) {
      const didRemoveExcludedItems = await this.#removeExcludedItems(event.detail?.resource);
      if (didRemoveExcludedItems) return;
    }

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      applyCartSectionsResponse(
        { sections: event.detail.data.sections },
        {
          sourceSectionId: this.sectionId,
          source: event.detail?.data?.source,
          resource: event.detail?.resource,
          itemCount: event.detail?.data?.itemCount,
          dispatchEvent: false,
        }
      );

      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async #removeExcludedItems(cartResource = null) {
    if (this.#isRemovingExcludedItems) return false;

    const hasInlineItems = !!cartResource && Array.isArray(cartResource.items);
    let cartData = cartResource;

    if (!hasInlineItems) {
      try {
        const response = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!response.ok) return false;
        cartData = await response.json();
      } catch {
        return false;
      }
    }

    if (!cartData || !Array.isArray(cartData.items)) return false;

    /** @type {Record<string, number>} */
    const updates = {};

    for (const item of cartData.items) {
      if (!this.#isExcludedItem(item)) continue;
      if (!item?.key) continue;
      updates[item.key] = 0;
    }

    if (Object.keys(updates).length === 0) return false;

    return enqueueCartMutation(async () => {
      this.#isRemovingExcludedItems = true;
      setCartItemsLocked(true);

      try {
        const sectionsParam = collectCartSectionIds(this.sectionId);

        const parsedResponseText = await requestCartJson(Theme.routes.cart_update_url, {
          updates,
          sections: sectionsParam,
          sections_url: window.location.pathname,
        });

        resetShimmer(this);

        if (parsedResponseText.errors) return true;

        this.#updateQuantitySelectors(parsedResponseText);

        const itemCount = this.#getNonExcludedItemCount(parsedResponseText.items);

        const morphed = applyCartSectionsResponse(parsedResponseText, {
          sourceSectionId: this.sectionId,
          source: 'cart-items-deposit-cleanup',
          itemCount,
          eventTarget: this,
        });

        if (!morphed) {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
        }

        this.#updateCartQuantitySelectorButtonStates();

        return true;
      } catch (error) {
        console.error(error);
        return false;
      } finally {
        setCartItemsLocked(false);
        this.#isRemovingExcludedItems = false;
      }
    });
  }

  #isExcludedItem(item) {
    const productType = String(item?.product_type || item?.product?.type || '')
      .trim()
      .toLowerCase();
    return productType === this.#excludedProductType;
  }

  #getNonExcludedItemCount(items) {
    let count = 0;
    for (const item of items || []) {
      if (this.#isExcludedItem(item)) continue;
      count += Number(item?.quantity || 0);
    }
    return count;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
